import { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback } from "react";
import { flushSync } from "react-dom";

interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  companyName: string | null;
  role: string;
  accountStatus: string;
  npiNumber: string | null;
  deaNumber: string | null;
  stateLicense: string | null;
  deaExpiration: string | null;
  stateLicenseExpiration: string | null;
  phone: string | null;
  shippingAddress: string | null;
  billingAddress: string | null;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string; user?: User; requiresPasswordChange?: boolean }>;
  setInitialPassword: (email: string, currentPassword: string, newPassword: string) => Promise<{ success: boolean; error?: string; user?: User }>;
  register: (data: RegisterData) => Promise<{ success: boolean; error?: string; autoApproved?: boolean }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  companyName: string;
  phone: string;
  billingAddress?: string;
  npiNumber: string;
  deaNumber: string;
  stateLicense: string;
  deaExpiration?: string;
  stateLicenseExpiration?: string;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  // Used to cancel stale in-flight refreshUser calls when login() completes.
  // If the user submits the login form before the initial /api/auth/me response
  // arrives, the stale response must not overwrite the user that login() set.
  const authVersionRef = useRef(0);

  const refreshUser = useCallback(async () => {
    const myVersion = authVersionRef.current;
    try {
      const res = await fetch("/api/auth/me", { credentials: "include" });
      // A login() call happened while we were in-flight — its setUser wins.
      if (authVersionRef.current !== myVersion) return;
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
      } else {
        setUser(null);
      }
    } catch {
      if (authVersionRef.current === myVersion) setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = async (email: string, password: string) => {
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (res.ok) {
        // Bump the version before setUser so any in-flight refreshUser call
        // that completes after this point will see the mismatch and bail out
        // instead of overwriting the authenticated user with null.
        authVersionRef.current++;
        // flushSync forces React to commit these state updates synchronously
        // before control returns to the caller. This is critical: React Router's
        // navigate() bypasses React's automatic batching and fires its own
        // re-render immediately. Without flushSync, AdminGuard would render
        // with the old user=null state and redirect back to /login before
        // the setUser batch commits.
        flushSync(() => {
          setUser(data.user);
          setLoading(false);
        });
        return { success: true, user: data.user };
      }
      if (res.status === 403 && data.requiresPasswordChange) {
        return { success: false, requiresPasswordChange: true, error: data.error };
      }
      return { success: false, error: data.error };
    } catch {
      return { success: false, error: "Network error" };
    }
  };

  const setInitialPassword = async (email: string, currentPassword: string, newPassword: string) => {
    try {
      const res = await fetch("/api/auth/set-initial-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, currentPassword, newPassword }),
      });
      const data = await res.json();
      if (res.ok) {
        flushSync(() => { setUser(data.user); });
        return { success: true, user: data.user };
      }
      return { success: false, error: data.error };
    } catch {
      return { success: false, error: "Network error" };
    }
  };

  const register = async (regData: RegisterData) => {
    try {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(regData),
      });
      const data = await res.json();
      if (res.ok) {
        await refreshUser();
        return { success: true, autoApproved: data.autoApproved };
      }
      return { success: false, error: data.error };
    } catch {
      return { success: false, error: "Network error" };
    }
  };

  const logout = async () => {
    await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, setInitialPassword, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
